import { useEffect } from "react";

const REVEAL_CLASSES = ["sr", "sr-left", "sr-right", "sr-zoom"];

/**
 * Reveal-on-scroll, sans coût sur le LCP.
 *
 * Garanties :
 *  - Le contenu est visible par défaut (SSR + no-JS). Aucune opacité 0 ne
 *    dépend du JavaScript pour du contenu déjà à l'écran.
 *  - Les éléments déjà visibles au premier rendu sont définitivement
 *    « dé-révélés » (classes retirées) AVANT que `.js-reveal` ne soit posé
 *    sur <html> : ils ne clignotent jamais et n'attendent pas l'hydratation.
 *  - Toutes les lectures de layout (getBoundingClientRect) sont regroupées
 *    dans une seule frame, avant toute écriture — pas de forced reflow.
 */
export function useReveal() {
  useEffect(() => {
    const root = document.documentElement;
    const els = Array.from(document.querySelectorAll<HTMLElement>("[data-reveal]"));
    if (!els.length) return;

    if (typeof IntersectionObserver === "undefined") {
      els.forEach((el) => el.removeAttribute("data-reveal"));
      return;
    }

    let io: IntersectionObserver | null = null;
    let fallback = 0;

    const raf = requestAnimationFrame(() => {
      const vh = window.innerHeight;
      // --- Phase LECTURE (aucune écriture ici) ---
      const measures = els.map((el) => {
        const rect = el.getBoundingClientRect();
        return { el, visible: rect.top < vh && rect.bottom > 0 };
      });

      // --- Phase ÉCRITURE ---
      const below: HTMLElement[] = [];
      for (const { el, visible } of measures) {
        if (visible) {
          // Above the fold : plus aucune animation pilotée par JS.
          el.classList.remove(...REVEAL_CLASSES);
          el.removeAttribute("data-reveal");
          el.style.transitionDelay = "";
        } else {
          below.push(el);
        }
      }

      if (!below.length) return;
      root.classList.add("js-reveal");

      io = new IntersectionObserver(
        (entries) => {
          entries.forEach((e) => {
            if (e.isIntersecting) {
              e.target.classList.add("in");
              io?.unobserve(e.target);
            }
          });
        },
        { threshold: 0.05, rootMargin: "0px 0px -10% 0px" },
      );
      below.forEach((el) => io!.observe(el));

      // Filet de sécurité : rien ne reste invisible.
      fallback = window.setTimeout(() => {
        document.querySelectorAll("[data-reveal]:not(.in)").forEach((el) => {
          el.classList.add("in");
        });
      }, 1500);
    });

    return () => {
      cancelAnimationFrame(raf);
      io?.disconnect();
      window.clearTimeout(fallback);
    };
  }, []);
}
